/**
 * API client for our own Merkl data endpoints
 * Replaces the external Merkl API with our internal scraped data
 */

interface LeaderboardEntry {
  recipient: string
  amount: string
  claimed: string
  pending: string
  token: {
    address: string
    decimals: number
    symbol: string
  }
  chain: {
    id: number
    name: string
  }
}

interface LeaderboardResponse {
  data: LeaderboardEntry[]
  total: number
  page: number
  items: number
}

interface RewardBreakdown {
  reason: string
  amount: string
  claimed: string
  pending: string
  campaignId: string
}

interface UserReward {
  root: string
  recipient: string
  amount: string
  claimed: string
  pending: string
  proofs: string[]
  token: {
    address: string
    chainId: number
    symbol: string
    decimals: number
    price: null
  }
  breakdowns: RewardBreakdown[]
}

interface ChainInfo {
  id: number
  name: string
  icon: string
}

interface UserRewardsResponse {
  chain: ChainInfo
  rewards: UserReward[]
}

class MerklApiClient {
  async getLeaderboard(params: {
    address: string
    chainId: number
    items?: number
    page?: number
  }): Promise<LeaderboardResponse> {
    const searchParams = new URLSearchParams({
      address: params.address,
      chainId: params.chainId.toString(),
      ...(params.items && { items: params.items.toString() }),
      ...(params.page && { page: params.page.toString() }),
    })

    const response = await fetch(`/api/merkl/leaderboard?${searchParams}`)

    if (!response.ok) {
      throw new Error(`Failed to fetch leaderboard: ${response.status} ${response.statusText}`)
    }

    return response.json()
  }

  async getUserRewards(params: {
    chainId: number
    test?: boolean
  }): Promise<UserRewardsResponse> {
    const searchParams = new URLSearchParams({
      chainId: params.chainId.toString(),
      ...(params.test && { test: params.test.toString() }),
    })

    const response = await fetch(`/api/merkl/users/rewards?${searchParams}`)

    if (!response.ok) {
      throw new Error(`Failed to fetch user rewards: ${response.status} ${response.statusText}`)
    }

    const data: UserRewardsResponse[] = await response.json()
    return data[0] || { chain: { id: params.chainId, name: '', icon: '' }, rewards: [] }
  }
}

// Export singleton instance
export const merklApiClient = new MerklApiClient()

// Export types for use in other files
export type { LeaderboardEntry, LeaderboardResponse, RewardBreakdown, UserReward, UserRewardsResponse, ChainInfo }
